import { get } from "@ember/object"
import Service, { inject as service } from "@ember/service"
import { isNone } from "@ember/utils"
import type { CardStack } from "@shared/models/card-stack"
import type { Planbook } from "@shared/models/planbook"
import { task, timeout } from "ember-concurrency"
import _ from "lodash"
import reorderPositionedList from "../lib/reorder-positioned-list"

interface ModelsNeededForRoutineFirstEdit {
  planbook: Planbook
  routine: CardStack
  dateString: string
}

export default class RoutineEditor extends Service {
  @service dispatcher

  editCardValue(modelsNeededForFirstEdit: ModelsNeededForRoutineFirstEdit, cardId, value) {
    this._checkForRoutineFirstEdit(
      modelsNeededForFirstEdit.planbook,
      modelsNeededForFirstEdit.routine,
      modelsNeededForFirstEdit.dateString
    )
    // @ts-ignore
    this.intermediateUpdate.perform({
      planbookId: modelsNeededForFirstEdit.planbook.id,
      cardStackId: modelsNeededForFirstEdit.routine.id,
      cardId: cardId,
      value: value,
    })
  }

  finishEditingCardValue(
    modelsNeededForFirstEdit: ModelsNeededForRoutineFirstEdit,
    cardId,
    value,
    originalValue
  ) {
    // @ts-ignore
    this.intermediateUpdate.cancelAll()

    this._checkForRoutineFirstEdit(
      modelsNeededForFirstEdit.planbook,
      modelsNeededForFirstEdit.routine,
      modelsNeededForFirstEdit.dateString
    )

    this.dispatcher.dispatch("ROUTINE_UPDATE_CARD_VALUE", {
      cardStackId: modelsNeededForFirstEdit.routine.id,
      planbookId: modelsNeededForFirstEdit.planbook.id,
      cardId: cardId,
      value: value,
      originalValue: originalValue,
    })
  }

  removeCard(modelsNeededForFirstEdit, card) {
    this._checkForRoutineFirstEdit(
      modelsNeededForFirstEdit.planbook,
      modelsNeededForFirstEdit.routine,
      modelsNeededForFirstEdit.dateString
    )
    this.dispatcher.dispatch("ROUTINE_REMOVE_SECTION", {
      cardStackId: modelsNeededForFirstEdit.routine.id,
      planbookId: modelsNeededForFirstEdit.planbook.id,
      card: card,
    })
  }

  /**
   * Reorder the cards in a routine.
   * TODO this should be combined with the lessonEditor._moveWithinLesson
   * as most of it is doing the same thing. I'd probably extract the middle part to a function
   * that returns positionDiffs gien the oldList, newList, and movedItem
   */
  reorderCards(planbook, routine, dateString, newList, movedItem) {
    // We get a ton of false positives, so find out if the order changes
    // kinda odd, but we use _.min to find out if there's a false as
    // in a list of booleans, false is lower, so
    let existingCards = _.sortBy(get(routine, "attributes.cards"), "attributes.position")
    let thingsHaveNotMoved = _.min(
      _.map(newList, (card, index) => {
        return existingCards[index] && existingCards[index].id === card.id
      })
    )
    if (thingsHaveNotMoved) return

    // Remove old section
    let newSections = _.map(newList, (card) => {
      if (card.id === movedItem.id) {
        let movingSection = _.cloneDeep(card)
        movingSection.attributes.position = null
        return movingSection
      } else {
        return card
      }
    })
    let newCardPositions = reorderPositionedList(newSections)

    let positionDiffs = _.reject(newCardPositions, (tuple) => {
      let section = _.find(newSections, { id: tuple[0] })
      return section.attributes.position === tuple[1]
    })

    this._checkForRoutineFirstEdit(planbook, routine, dateString)

    get(this, "dispatcher").dispatch("ROUTINE_REORDER_SECTIONS", {
      cardStackId: routine.id,
      positionDiffs: positionDiffs,
      sectionBeingMoved: movedItem,
      planbookId: planbook.id,
    })
  }

  intermediateUpdate = task(
    { keepLatest: true },
    async ({ cardStackId, planbookId, cardId, value }) => {
      this.dispatcher.dispatch("ROUTINE_INTERMEDIATE_UPDATE_CARD_VALUE", {
        cardStackId: cardStackId,
        planbookId: planbookId,
        cardId: cardId,
        value: value,
      })
      await timeout(750)
    }
  )

  /**
   * Check if the routine was generated from a template and we need to create it.
   * @param  {Planbook} planbook
   * @param  {CardStack} routine
   * @return undefined
   */
  _checkForRoutineFirstEdit(planbook, routine, dateString) {
    let planbookDate = _.chain(planbook.attributes.calendar.dates)
      .find((date) => date.attributes.cardStackId === routine.id)
      .value()

    if (isNone(planbookDate) || isNone(planbookDate.attributes.cardStackId)) {
      get(this, "dispatcher").dispatch("ROUTINE_FIRST_EDIT", {
        planbook: planbook,
        dateString: dateString,
        cardStackId: routine.id,
        cards: routine.attributes.cards,
      })
    }
  }
}
